// FICTIONAL sample team for the no-signup demo (/demo). Every name and number here is made up.
// Shots are generated first, then each box score is built from them, so stats, shot chart and hot zones agree.
// The combine benchmark is the real GAMEDAY comparison object for shooting guards (NBA Draft Combine sample).
import { classify } from "./court";

const rng = (seed) => () => {
  seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

export const DEMO_TEAM = { id: "demo-team", name: "Riverside Hawks", level: "Varsity", city: "Sample City", coach: "Coach Alex Rivera (sample)" };

// [id, name, jersey, positions, grad, height_in, weight_lb, gpa, archetype, 2PA, 2P%, 3PA, 3P%, FTA, FT%, REB, AST, STL, BLK]
const ROWS = [
  ["demo-p1", "Jordan Ellis", "11", ["SG"], 2027, 75, 180, 3.6, "Three-Level Scorer", 9, 0.52, 6, 0.37, 6, 0.82, 4.5, 3.2, 1.6, 0.4],
  ["demo-p2", "Marcus Reed", "4", ["PG"], 2026, 72, 165, 3.4, "Floor General", 6, 0.47, 3, 0.34, 2, 0.8, 3.5, 7.4, 2.1, 0.2],
  ["demo-p3", "Tyler Brooks", "23", ["SF"], 2027, 78, 195, 3.1, "Two-Way Wing", 5, 0.48, 3, 0.35, 2, 0.74, 5.8, 2.1, 1.8, 0.7],
  ["demo-p4", "Darius Coleman", "32", ["PF"], 2026, 80, 215, 2.9, "Stretch Big", 5, 0.47, 2, 0.33, 2, 0.7, 7.6, 1.5, 0.6, 1.2],
  ["demo-p5", "Isaiah Grant", "50", ["C"], 2028, 82, 230, 3.2, "Rim Protector", 8, 0.55, 0, 0, 3, 0.6, 10.8, 1.2, 0.5, 2.8],
  ["demo-p6", "Caleb Morris", "2", ["PG"], 2028, 71, 160, 3.8, "Shot Creator", 2, 0.42, 2, 0.31, 1, 0.85, 2.0, 2.4, 1.0, 0.1],
  ["demo-p7", "Andre Watts", "15", ["SG"], 2027, 74, 175, 3.0, "Slasher", 3, 0.47, 1, 0.28, 2, 0.68, 3.0, 1.8, 1.2, 0.3],
  ["demo-p8", "Noah Bennett", "21", ["SF", "PF"], 2028, 77, 185, 3.5, "Point Forward", 3, 0.45, 1, 0.3, 1, 0.72, 4.4, 3.6, 0.8, 0.5],
];

export const DEMO_GAMES = [
  ["demo-g1", "2026-11-21", "Lakeview Lions", true], ["demo-g2", "2026-11-25", "North Ridge Rams", false],
  ["demo-g3", "2026-12-02", "Westbrook Wolves", true], ["demo-g4", "2026-12-06", "Harbor City Hornets", true],
  ["demo-g5", "2026-12-12", "Pine Valley Panthers", false], ["demo-g6", "2026-12-16", "East End Eagles", true],
].map(([id, date, opponent, home]) => ({ id, date, opponent, home }));

const HOOP = [25, 5.25];
const polar = (r, a) => [HOOP[0] + r * Math.cos(a), HOOP[1] + r * Math.sin(a)];
const spot = (zone, R) => {
  const pick = (lo, hi) => lo + (hi - lo) * R();
  switch (zone) {
    case "rim": return polar(pick(0.5, 3.5), pick(0.1, Math.PI - 0.1));
    case "paint": return [pick(19.5, 30.5), pick(10, 18.5)];
    case "mid": return polar(pick(12, 18.5), pick(0.2, Math.PI - 0.2));
    case "corner": return [R() < 0.5 ? pick(1.5, 4.5) : pick(45.5, 48.5), pick(1, 9)];
    case "wing": return polar(pick(21, 24), R() < 0.5 ? pick(0.25, 0.38) * Math.PI : pick(0.62, 0.75) * Math.PI);
    default: return polar(pick(21, 25), pick(0.4, 0.6) * Math.PI);
  }
};
const TWO = [["rim", 0.45], ["paint", 0.25], ["mid", 0.3]];
const THREE = [["corner", 0.3], ["wing", 0.45], ["top", 0.25]];
const choose = (list, R) => { let x = R(); for (const [z, w] of list) { if ((x -= w) <= 0) return z; } return list[0][0]; };
const vary = (base, R) => Math.max(0, Math.round(base * (0.75 + 0.5 * R())));

const buildPlayer = (row, i) => {
  const [id, name, jersey, positions, grad_year, height_in, weight_lb, gpa, primary_archetype, a2, p2, a3, p3, fta, pft, reb, ast, stl, blk] = row;
  const R = rng(1000 + i * 97);
  const shots = [];
  const perGame = DEMO_GAMES.map((g) => {
    const gShots = [];
    for (const [n, rate, list] of [[vary(a2, R), p2, TWO], [vary(a3, R), p3, THREE]]) {
      for (let k = 0; k < n; k += 1) {
        const [x, y] = spot(choose(list, R), R);
        const [points, zone] = classify(Math.min(49.5, Math.max(0.5, x)), Math.min(46, Math.max(0.5, y)));
        gShots.push({ id: `${id}-${g.id}-${k}-${points}`, game_id: g.id, x, y, points, zone, made: R() < rate + (zone === "rim" ? 0.1 : 0), opponent: g.opponent, date: g.date });
      }
    }
    shots.push(...gShots);
    const f = vary(fta, R);
    const ftm = Math.round(f * pft * (0.85 + 0.3 * R()));
    const l = { fg2m: 0, fg2a: 0, fg3m: 0, fg3a: 0, ftm: Math.min(f, ftm), fta: f, reb: vary(reb, R), ast: vary(ast, R), stl: vary(stl, R), blk: vary(blk, R), tov: vary(2, R) };
    gShots.forEach((s) => { l[`fg${s.points}a`] += 1; if (s.made) l[`fg${s.points}m`] += 1; });
    const fgm = l.fg2m + l.fg3m, fga = l.fg2a + l.fg3a;
    l.pts = 2 * l.fg2m + 3 * l.fg3m + l.ftm;
    l.eff = l.pts + l.reb + l.ast + l.stl + l.blk - (fga - fgm) - (l.fta - l.ftm) - l.tov;
    return { ...l, game_id: g.id, date: g.date, opponent: g.opponent };
  });
  const sum = (k) => perGame.reduce((t, g) => t + g[k], 0);
  const gp = perGame.length;
  const pct = (m, a) => (a ? Math.round((1000 * m) / a) / 10 : null);
  const avg = (k) => Math.round((10 * sum(k)) / gp) / 10;
  const fgm = sum("fg2m") + sum("fg3m"), fga = sum("fg2a") + sum("fg3a");
  const zones = {};
  shots.forEach((s) => { const z = (zones[s.zone] ??= { m: 0, a: 0 }); z.a += 1; z.m += s.made ? 1 : 0; });
  const season = {
    gp, ppg: avg("pts"), rpg: avg("reb"), apg: avg("ast"), spg: avg("stl"), bpg: avg("blk"), mpg: 26 + (i % 3) * 3,
    fg_pct: pct(fgm, fga), fg3_pct: pct(sum("fg3m"), sum("fg3a")), ft_pct: pct(sum("ftm"), sum("fta")),
    efg_pct: pct(fgm + 0.5 * sum("fg3m"), fga), ts_pct: pct(sum("pts"), 2 * (fga + 0.44 * sum("fta"))),
    eff: sum("eff"), ast_to: Math.round((10 * sum("ast")) / Math.max(1, sum("tov"))) / 10, zones,
    fgm, fga, fg2m: sum("fg2m"), fg2a: sum("fg2a"), fg3m: sum("fg3m"), fg3a: sum("fg3a"), ftm: sum("ftm"), fta: sum("fta"), pts: sum("pts"),
  };
  return { id, name, jersey, positions, grad_year, height_in, weight_lb, gpa, primary_archetype, status: "active", season, perGame, shots };
};

export const DEMO_PLAYERS = ROWS.map(buildPlayer);
export const demoPlayer = (id) => DEMO_PLAYERS.find((p) => p.id === id);

// Final scores: our points are the sum of the box scores; opponent scores are fixed sample values.
const THEM = [58, 71, 49, 62, 69, 55];
DEMO_GAMES.forEach((g, i) => {
  g.us = DEMO_PLAYERS.reduce((t, p) => t + p.perGame[i].pts, 0);
  g.them = THEM[i];
});
export const DEMO_RECORD = DEMO_GAMES.reduce((r, g) => (g.us > g.them ? { ...r, w: r.w + 1 } : { ...r, l: r.l + 1 }), { w: 0, l: 0 });

export const shotSummary = (shots, ftm = 0, fta = 0) => {
  const made = (l) => l.filter((s) => s.made).length;
  const two = shots.filter((s) => s.points === 2), three = shots.filter((s) => s.points === 3);
  const pct = (m, a) => (a ? (100 * m) / a : null);
  const fgm = made(shots), fga = shots.length, pts = 2 * made(two) + 3 * made(three) + ftm;
  return { fgm, fga, fg_pct: pct(fgm, fga), fg2m: made(two), fg2a: two.length, fg2_pct: pct(made(two), two.length),
    fg3m: made(three), fg3a: three.length, fg3_pct: pct(made(three), three.length), ftm, fta, ft_pct: pct(ftm, fta), pts,
    ts_pct: fga + fta ? (100 * pts) / (2 * (fga + 0.44 * fta)) : null, efg_pct: pct(fgm + 0.5 * made(three), fga) };
};

// Real GAMEDAY benchmark object (shooting guards). Sample sessions below are fictional.
export const SG_COMPARISON = {
  position: "SG", label: "Shooting Guard", sample_label: "Avg. of NBA-drafted players, 2000\u20132018 combine sample",
  sources: [{ label: "NBA Draft Combine measurements (official)", url: "https://www.nba.com/stats/draft/combine" }],
  metrics: { height_with_shoes_in: 77.0, weight_lb: 200.0, wingspan_in: 81.0, standing_reach_in: 102.0, body_fat_pct: 6.5, standing_vert_in: 30.0,
    running_vert_in: 35.5, bench_reps: 9.0, lane_agility_s: 11.1, three_quarter_sprint_s: 3.25, reactive_shuttle_s: 3.0 },
};
export const DEMO_COMBINE = {
  "demo-p1": [
    { id: "demo-c1", date: "2026-08-15", height_with_shoes_in: 75.5, weight_lb: 176, wingspan_in: 78.5, standing_reach_in: 99, standing_vert_in: 27.5, running_vert_in: 32, lane_agility_s: 11.6, three_quarter_sprint_s: 3.38 },
    { id: "demo-c2", date: "2026-11-10", height_with_shoes_in: 75.75, weight_lb: 180, wingspan_in: 78.5, standing_reach_in: 99.5, standing_vert_in: 28.5, running_vert_in: 33.5, lane_agility_s: 11.4, three_quarter_sprint_s: 3.33 },
  ],
};
export const combineFor = (id) => {
  const sessions = DEMO_COMBINE[id] ?? [];
  if (!sessions.length) return null;
  const lower = new Set(["lane_agility_s", "three_quarter_sprint_s", "reactive_shuttle_s", "body_fat_pct"]);
  const bests = {};
  sessions.forEach((s) => Object.entries(s).forEach(([k, v]) => {
    if (typeof v !== "number") return;
    bests[k] = bests[k] == null ? v : lower.has(k) ? Math.min(bests[k], v) : Math.max(bests[k], v);
  }));
  return { latest: sessions[sessions.length - 1], bests, sessions: [...sessions].reverse(), comparison: SG_COMPARISON };
};
